import FormikInput from 'components/formikFields/input'
import Header from 'components/headers/wallet'
import { Form, Formik, useFormikContext } from 'formik'
import React, { useEffect, useRef, useState } from 'react'
import { IoChevronBack, IoClose } from 'react-icons/io5'
import { Schema } from './utilities'
import SelectModal from 'components/modals/selectModal'
import useSetAddUser from './hooks/useSetAddUser'
import useJoinLink from '../../../pages/fund/joinFundByLink/hooks/useJoinLink'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
interface ISelectUser {
    name: string
    id: undefined | number
    memberId: undefined | number
}
type TPickerRole = 'guarantor' | 'recommender'
interface IFormValues {
    phone_number: string
    number_of_shares: string
    first_name: string
    last_name: string
}

/**
 * Says why «افزودن عضو» did not submit.
 *
 * Formik never calls onSubmit while a field is invalid, and the field's own
 * message sits under it — on a phone, usually under the keyboard or above the
 * fold. So a mistyped number looked exactly like a dead button — «اتفاقی
 * نمی‌افتد», in the manager's words.
 */
function SubmitFailureNotice({ fundId }: { fundId?: string }) {
    const { submitCount, isSubmitting, errors } =
        useFormikContext<IFormValues>()
    const handled = useRef(0)
    useEffect(() => {
        if (isSubmitting || submitCount === handled.current) {
            return
        }
        handled.current = submitCount
        const fields = Object.keys(errors) as Array<keyof IFormValues>
        if (fields.length === 0) {
            return
        }
        posthog.capture('member_add_validation_failed', {
            fund_id: fundId,
            fields,
        })
        const first = fields.map((f) => errors[f]).find(Boolean)
        toast.error(first || 'اطلاعات فرم را بررسی کنید.')
    }, [submitCount, isSubmitting, errors, fundId])
    return null
}

function PickerField({
    label,
    selected,
    onOpen,
    onClear,
}: {
    label: string
    selected?: ISelectUser
    onOpen: () => void
    onClear: () => void
}) {
    return (
        <div className=" mx-3 mt-4 text-sm font-normal">
            <div
                role="button"
                className=" bg-transparent w-full relative rounded-2xl py-3 px-4 border-1 border-gray-2 flex items-center justify-between gap-2 cursor-pointer"
                onClick={onOpen}
            >
                <span className=" absolute right-5 -top-3 bg-white pl-1">
                    {label}
                </span>
                {selected?.name ? (
                    <>
                        <p className=" text-black truncate">{selected.name}</p>
                        <button
                            type="button"
                            aria-label={`حذف ${label}`}
                            className=" -m-2 p-2 shrink-0 text-gray-2"
                            onClick={(e) => {
                                e.stopPropagation()
                                onClear()
                            }}
                        >
                            <IoClose className="w-4 h-4" />
                        </button>
                    </>
                ) : (
                    <>
                        <p className=" text-gray-2">
                            اختیاری — انتخاب از اعضای صندوق
                        </p>
                        <IoChevronBack className="w-4 h-4 shrink-0 text-gray-2" />
                    </>
                )}
            </div>
        </div>
    )
}

export default function AddNewUser() {
    const [isOpenGuarantor, setIsOpenGuarantor] = useState<boolean>(false)
    const [selectedGuarantor, setSelectedGuarantor] = useState<ISelectUser>()
    const [isOpenRecommender, setIsOpenRecommender] = useState<boolean>(false)
    const [selectedRecommender, setSelectedRecommender] =
        useState<ISelectUser>()
    const { id } = useParams()
    const { joinLink } = useJoinLink(id)
    const { mutate, isLoading } = useSetAddUser()
    // SelectModal wants a member-id setter; this screen only needs the
    // poolship id it gets through setSelectedUser.
    const ignoreMemberId = () => undefined

    // Whether the open picker ended in a pick — read when it closes, so a
    // picker dismissed empty-handed shows up in PostHog.
    const picked = useRef(false)
    const openPicker = (role: TPickerRole) => {
        picked.current = false
        posthog.capture('member_add_picker_opened', { fund_id: id, role })
        if (role === 'guarantor') {
            setIsOpenGuarantor(true)
        } else {
            setIsOpenRecommender(true)
        }
    }
    const closePicker = (role: TPickerRole) => {
        if (!picked.current) {
            posthog.capture('member_add_picker_dismissed', {
                fund_id: id,
                role,
            })
        }
        if (role === 'guarantor') {
            setIsOpenGuarantor(false)
        } else {
            setIsOpenRecommender(false)
        }
    }
    const pickerSetter =
        (
            role: TPickerRole,
            set: React.Dispatch<React.SetStateAction<ISelectUser | undefined>>
        ): React.Dispatch<React.SetStateAction<ISelectUser | undefined>> =>
        (user) => {
            picked.current = true
            posthog.capture('member_add_picker_selected', {
                fund_id: id,
                role,
            })
            set(user)
        }

    return (
        <>
            {/* The form used to wait for the join link and showed nothing but
                a spinner until it came back — a slow or failed link request
                left the manager with no form at all. The link is a
                convenience; only its own field waits for it now. */}
            <Formik<IFormValues>
                initialValues={{
                    phone_number: '',
                    number_of_shares: '1',
                    first_name: '',
                    last_name: '',
                }}
                validationSchema={Schema}
                onSubmit={(values) => {
                    // The button is disabled while a request is out, but Enter
                    // in a field still submits the form.
                    if (isLoading) {
                        return
                    }
                    const payload: IAddNewUserToFundPayload = {
                        moneyPool: id,
                        phone_number: values.phone_number,
                        first_name: values.first_name,
                        last_name: values.last_name,
                        number_of_shares: values.number_of_shares,
                        recommender: selectedRecommender?.id,
                        guarantor: selectedGuarantor?.id,
                    }
                    posthog.capture('member_add_submitted', {
                        fund_id: id,
                        number_of_shares: values.number_of_shares,
                        has_guarantor: Boolean(selectedGuarantor),
                        has_recommender: Boolean(selectedRecommender),
                    })
                    mutate(payload)
                }}
            >
                <Form>
                    <SubmitFailureNotice fundId={id} />
                    <div className=" h-screen flex flex-col">
                        <div className=" grow-0">
                            <Header title="عضو جدید" />
                        </div>
                        <div className=" grow mt-32">
                            <div className=" mx-3 mt-3 text-sm font-normal">
                                <FormikInput
                                    type="number"
                                    label="شماره موبایل"
                                    name="phone_number"
                                    placeholder=""
                                    className="text-sm font-normal"
                                    isGray
                                    min={0}
                                />
                            </div>
                            <div className=" mx-3 mt-4 text-sm font-normal grid grid-cols-3 gap-2">
                                <div className=" col-span-1">
                                    <FormikInput
                                        type="text"
                                        label="نام"
                                        name="first_name"
                                        placeholder=""
                                        className="text-sm font-normal"
                                        isGray
                                    />
                                </div>
                                <div className=" col-span-2">
                                    <FormikInput
                                        type="text"
                                        label="نام خانوادگی"
                                        name="last_name"
                                        placeholder=""
                                        className="text-sm font-normal"
                                        isGray
                                    />
                                </div>
                            </div>
                            <div className=" mx-3 mt-4 text-sm font-normal text-center items-center">
                                <div className=" col-span-1">
                                    <FormikInput
                                        type="number"
                                        label="تعداد سهم"
                                        name="number_of_shares"
                                        placeholder=""
                                        className="text-sm font-normal"
                                        isGray
                                        min={0}
                                    />
                                </div>
                            </div>
                            <PickerField
                                label="نام ضامن عضو"
                                selected={selectedGuarantor}
                                onOpen={() => openPicker('guarantor')}
                                onClear={() => setSelectedGuarantor(undefined)}
                            />
                            <PickerField
                                label="نام معرف عضو"
                                selected={selectedRecommender}
                                onOpen={() => openPicker('recommender')}
                                onClear={() =>
                                    setSelectedRecommender(undefined)
                                }
                            />
                            {joinLink && (
                                <div className=" mx-3 mt-3 text-xs font-thin">
                                    <FormikInput
                                        type="text"
                                        label="لینک عضویت(برای کپی کردن کلیک کنید)"
                                        name="join_number"
                                        placeholder=""
                                        className="text-sm font-normal"
                                        value={joinLink}
                                        isGray
                                        readOnly
                                        onClick={() => {
                                            try {
                                                navigator.clipboard
                                                    .writeText(joinLink)
                                                    .then((r) =>
                                                        toast.success(
                                                            'لینک با موفقیت کپی شد'
                                                        )
                                                    )
                                            } catch (err) {
                                                toast.error('مشکلی پیش آمده!')
                                                console.error(
                                                    'Failed to copy: ',
                                                    err
                                                )
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        <div className=" grow-0 mx-4">
                            <button
                                className=" w-full rounded-2xl bg-green-1 text-white py-3 my-3 disabled:opacity-60"
                                type="submit"
                                disabled={isLoading}
                                aria-busy={isLoading}
                            >
                                {isLoading
                                    ? 'در حال افزودن عضو…'
                                    : 'افزودن عضو'}
                            </button>
                        </div>
                    </div>
                </Form>
            </Formik>
            <SelectModal
                show={isOpenGuarantor}
                onHide={() => closePicker('guarantor')}
                setSelectedUser={pickerSetter(
                    'guarantor',
                    setSelectedGuarantor
                )}
                setMemberId={ignoreMemberId}
                title="انتخاب ضامن"
                selectOnTap
            />
            <SelectModal
                show={isOpenRecommender}
                onHide={() => closePicker('recommender')}
                setSelectedUser={pickerSetter(
                    'recommender',
                    setSelectedRecommender
                )}
                setMemberId={ignoreMemberId}
                title="انتخاب معرف"
                selectOnTap
            />
        </>
    )
}
